/**
 * The terminal side of the hub protocol: a small `fetch` client the staff app (and the
 * shells' web layer) uses to submit commands, read and act on KDS tickets, ask for prints
 * and listen to what the hub broadcasts. Browser-safe: no Node imports.
 */
import type { KdsTicketsView } from './client-types.js';
import {
  hubInfoSchema,
  submitResultSchema,
  printStatusSchema,
  type HubEvent,
  type HubInfo,
  type PrintStatus,
  type SubmitCommand,
  type SubmitResult,
} from './protocol.js';

export type { HubEvent, HubInfo, PrintStatus, SubmitCommand, SubmitResult } from './protocol.js';
export type { KdsTicketsView } from './client-types.js';

export class HubHttpError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    detail: string,
  ) {
    super(detail);
    this.name = 'HubHttpError';
  }
}

export interface HubClientOptions {
  /** `https://192.168.1.20:8443` from discovery. */
  baseUrl: string;
  terminalId: string;
  /** The terminal's token, issued when it was registered with the outlet. */
  token: string;
  fetch?: typeof fetch;
}

export class HubClient {
  readonly #f: typeof fetch;

  constructor(private readonly o: HubClientOptions) {
    this.#f = o.fetch ?? ((input, init) => fetch(input, init));
  }

  #headers(extra: Record<string, string> = {}): Record<string, string> {
    return {
      'x-terminal-id': this.o.terminalId,
      authorization: `Bearer ${this.o.token}`,
      ...extra,
    };
  }

  async #call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await this.#f(`${this.o.baseUrl}${path}`, {
      method,
      headers: this.#headers(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : null;
    if (!res.ok) {
      const p = (json ?? {}) as { code?: string; detail?: string };
      throw new HubHttpError(res.status, p.code ?? 'ERROR', p.detail ?? `HTTP ${res.status}`);
    }
    return json as T;
  }

  async info(): Promise<HubInfo> {
    const res = await this.#f(`${this.o.baseUrl}/hub/info`, {});
    if (!res.ok) throw new HubHttpError(res.status, 'ERROR', `HTTP ${res.status}`);
    return hubInfoSchema.parse(await res.json());
  }

  async submit(cmd: Omit<SubmitCommand, 'terminalId'>): Promise<SubmitResult> {
    return submitResultSchema.parse(
      await this.#call('POST', '/commands', { ...cmd, terminalId: this.o.terminalId }),
    );
  }

  kdsTickets(stationId: string | null): Promise<KdsTicketsView> {
    const q = stationId ? `?station=${encodeURIComponent(stationId)}` : '';
    return this.#call('GET', `/kds/tickets${q}`);
  }

  kdsAct(
    ticketId: string,
    action: 'start' | 'bump' | 'recall',
    lineIds?: readonly string[],
  ): Promise<unknown> {
    return this.#call('POST', `/kds/tickets/${encodeURIComponent(ticketId)}/action`, {
      action,
      ...(lineIds ? { lineIds } : {}),
    });
  }

  async openOrders(): Promise<Record<string, unknown>[]> {
    return (await this.#call<{ data: Record<string, unknown>[] }>('GET', '/orders/open')).data;
  }

  async print(jobId: string, printerId: string, doc: unknown): Promise<PrintStatus> {
    return printStatusSchema.parse(await this.#call('POST', '/print', { jobId, printerId, doc }));
  }

  async printStatus(jobId: string): Promise<PrintStatus> {
    return printStatusSchema.parse(await this.#call('GET', `/print/${encodeURIComponent(jobId)}`));
  }

  /** Jobs for printers this terminal owns that it has not confirmed. */
  async pendingPrints(): Promise<{ jobId: string; printerId: string; doc: unknown }[]> {
    return (
      await this.#call<{ data: { jobId: string; printerId: string; doc: unknown }[] }>(
        'GET',
        '/print/pending',
      )
    ).data;
  }

  confirmPrint(jobId: string, result: { ok: boolean; error?: string }): Promise<PrintStatus> {
    return this.#call('POST', `/print/${encodeURIComponent(jobId)}/result`, result);
  }

  /**
   * Listen to the hub's server-sent events until `signal` aborts or the stream ends.
   * Resolves with the last event id seen (pass it as `after` to resume).
   */
  async listen(
    topics: readonly string[],
    onEvent: (e: HubEvent) => void,
    opts: { after?: number; signal?: AbortSignal } = {},
  ): Promise<number> {
    const q = `topics=${topics.join(',')}${opts.after ? `&after=${opts.after}` : ''}`;
    const res = await this.#f(`${this.o.baseUrl}/events?${q}`, {
      headers: this.#headers(),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
    if (!res.ok || !res.body) throw new HubHttpError(res.status, 'ERROR', `HTTP ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let last = opts.after ?? 0;
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let cut = buf.indexOf('\n\n');
        while (cut >= 0) {
          const block = buf.slice(0, cut);
          buf = buf.slice(cut + 2);
          const data = block
            .split('\n')
            .filter((l) => l.startsWith('data: '))
            .map((l) => l.slice(6))
            .join('\n');
          if (data) {
            const e = JSON.parse(data) as HubEvent;
            last = e.id;
            onEvent(e);
          }
          cut = buf.indexOf('\n\n');
        }
      }
    } catch (error) {
      if (!opts.signal?.aborted) throw error;
    }
    return last;
  }
}
